// Progress bar component showing wizard steps

import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WizardStep, StoryType } from '../types';
import { WIZARD_STEPS, STEP_LABELS, visibleSteps } from '../constants';

interface StepIndicatorProps {
  currentStep: WizardStep;
  completedSteps: Set<WizardStep>;
  storyType: StoryType | null;
  onStepClick?: (step: WizardStep) => void;
  className?: string;
  steps?: WizardStep[];
}

export function StepIndicator({
  currentStep,
  completedSteps,
  storyType = 'single',
  onStepClick,
  className,
  steps: sequence,
}: StepIndicatorProps) {
  const steps = visibleSteps(sequence ?? (storyType ? WIZARD_STEPS[storyType] : WIZARD_STEPS.single));
  const currentStepIndex = Math.max(0, steps.indexOf(currentStep));
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  return (
    <div className={cn("w-full", className)}>
      {/* Desktop progress bar */}
      <div className="hidden sm:block relative mb-8">
        <div className="absolute top-5 left-0 w-full h-0.5 bg-gray-200" />
        <motion.div
          className="absolute top-5 left-0 h-0.5 bg-gradient-to-r from-purple-600 to-pink-600"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        />
        
        {/* Step indicators */}
        <div className="relative flex justify-between">
          {steps.map((step, index) => {
            const isCompleted = completedSteps.has(step);
            const isCurrent = step === currentStep;
            const isPast = index < currentStepIndex;
            const isClickable = isCompleted || isPast;
            
            return (
              <button
                key={step}
                onClick={() => isClickable && onStepClick?.(step)}
                disabled={!isClickable || !onStepClick}
                className={cn(
                  "relative flex flex-col items-center",
                  isClickable && onStepClick && "cursor-pointer",
                  (!isClickable || !onStepClick) && "cursor-default"
                )}
              >
                <motion.div
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200",
                    isCurrent && "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg scale-110",
                    isCompleted && !isCurrent && "bg-green-500 text-white",
                    !isCompleted && !isCurrent && isPast && "bg-purple-300 text-white",
                    !isCompleted && !isCurrent && !isPast && "bg-gray-200 text-gray-400"
                  )}
                  whileHover={isClickable && onStepClick ? { scale: 1.1 } : {}}
                  whileTap={isClickable && onStepClick ? { scale: 0.95 } : {}}
                >
                  {isCompleted && !isCurrent ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    index + 1
                  )}
                </motion.div>
                
                {/* Step label */}
                <span className={cn(
                  "absolute top-14 text-xs font-medium whitespace-nowrap",
                  isCurrent && "text-purple-600",
                  (isCompleted || isPast) && !isCurrent && "text-gray-700",
                  !isCompleted && !isPast && !isCurrent && "text-gray-400"
                )}>
                  {STEP_LABELS[step]}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Mobile-friendly step indicator - enhanced design */}
      <div className="sm:hidden">
        {/* Progress bar */}
        <div className="relative mb-4">
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-purple-600 to-pink-600"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            />
          </div>
        </div>
        
        {/* Step info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-semibold text-purple-600">
              Step {currentStepIndex + 1}
            </span>
            <span className="text-sm text-gray-500">of {steps.length}</span>
          </div>
          <span className="text-sm font-medium text-gray-700">
            {STEP_LABELS[currentStep]}
          </span>
        </div>
      </div>
    </div>
  );
}
